<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { formatNumber, formatNumberForScale } from "chartjs-graph";
import Modal from "@/ui/components/modal.vue";
import LocaleNumberInput from "@/ui/components/LocaleNumberInput.vue";
import { AverageCpmResult, solveAverageCpm } from "@/modules/base_price/utils/averageCpm";
import { PriceBounds } from "@/modules/base_price/utils/dayPricing";

/**
 * "Define average CPM" — a tool that writes a fill & CPM configuration, not a
 * setting of its own: the average typed here is not kept anywhere. Calculate
 * finds every month's fill and price so the year averages it with each month
 * still on budget and the months keeping their price pattern (see
 * solveAverageCpm()); Apply hands those to the month chart's draft, where
 * they are applied and saved like any other fill & CPM edit.
 *
 * The month figures are the target's own, but its year also counts whatever
 * below it decides its own fill or price — a child, a screen, a few days —
 * and those do not follow. So the first answer is checked against the
 * resolver (`evaluate`) and the goal nudged until the real average lands on
 * the one asked for, keeping the months' pattern throughout.
 */
const props = defineProps<{
	months: string[];
	gross: number[];
	budget: number[];
	/** The month chart's draft, as it stands. */
	fill: number[];
	cpm: number[];
	bounds: PriceBounds;
	/** The target's year average as resolved — what the plan really averages now. */
	currentAverage: number;
	/** Resolves the plan with these months applied and answers the target's real year average. */
	evaluate: (months: ({ fill: number; cpm: number } | null)[]) => Promise<number | null>;
}>();

const visible = defineModel<boolean>("visible", { default: false });

const emit = defineEmits<{
	/** Per month: the fill and the price to set, or null to leave a month as it is. */
	apply: [result: ({ fill: number; cpm: number } | null)[]];
}>();

const sellableNow = computed(() =>
	props.gross.map((gross, i) => (gross * (props.fill[i] ?? 0)) / 100)
);

const target = ref<number | null>(null);
const result = ref<AverageCpmResult | null>(null);
const calculating = ref(false);

// Opens on the current average, so Calculate straight away is a no-op and
// any change is a deliberate one.
watch(visible, (open) => {
	if (!open) return;

	target.value = Math.round(props.currentAverage * 100) / 100;
	result.value = null;
});

// A result belongs to the target it was calculated for.
watch(target, () => (result.value = null));

/** Within half a cent of the target counts as landed. */
const TOLERANCE = 0.005;
const MAX_ROUNDS = 6;

const solveFor = (goal: number) =>
	solveAverageCpm(
		props.months.map((_, i) => ({
			gross: props.gross[i] ?? 0,
			budget: props.budget[i] ?? 0,
			sellable: sellableNow.value[i],
		})),
		goal,
		props.bounds
	);

const asMonths = (solved: AverageCpmResult) =>
	solved.months.map((month) => (month ? { fill: month.fill, cpm: month.cpm } : null));

/**
 * Solves for the target, then asks the resolver what the year really
 * averages and scales the goal by how far off it was — the part that does not
 * follow stays as it is, so the part that does has to carry the difference.
 * A few rounds settle it; when the part that follows has nothing left to give
 * (every month sold out, or at a bound), the rounds stop moving and what is
 * shown is the closest the year gets.
 */
const calculate = async () => {
	const wanted = target.value;
	if (!wanted || wanted <= 0) return;

	calculating.value = true;

	let goal = wanted;
	let solved = solveFor(goal);
	let actual = await props.evaluate(asMonths(solved));

	for (
		let round = 1;
		round < MAX_ROUNDS && actual !== null && actual > 0 && Math.abs(actual - wanted) > TOLERANCE;
		round++
	) {
		goal *= wanted / actual;
		solved = solveFor(goal);
		actual = await props.evaluate(asMonths(solved));
	}

	calculating.value = false;

	// The target may have changed while the rounds were running.
	if (target.value !== wanted) return;

	const achieved = actual ?? solved.achieved;

	result.value = {
		...solved,
		achieved,
		reached: Math.abs(achieved - wanted) <= TOLERANCE,
	};
};

const apply = () => {
	if (!result.value) return;

	emit(
		"apply",
		result.value.months.map((month) => (month ? { fill: month.fill, cpm: month.cpm } : null))
	);
	visible.value = false;
};

const boundsText = computed(() => {
	const { min, max } = props.bounds;
	if (min === null && max === null) return null;

	return `between ${min !== null ? formatNumber(min, "money") : "no floor"} and ${
		max !== null ? formatNumber(max, "money") : "no ceiling"
	}`;
});
</script>

<template>
	<modal
		v-model:visible="visible"
		title="Define average CPM"
		icon="fa-light fa-calculator"
		width="46rem"
		:persistent="false"
	>
		<div class="text-body-2 text-medium-emphasis mb-4">
			Every month keeps its budget from Monthly Budget Distribution and its price
			relative to the other months; fill moves so the year averages the CPM you
			enter. That is not the same as one price for every month — a month that
			cannot sell enough at a flat price would fall short of its budget. The
			result replaces the months' fill and hand-set CPM in the chart, to apply
			and save like any other change.
		</div>

		<div class="d-flex align-end ga-4 flex-wrap mb-4">
			<div>
				<div class="text-caption text-medium-emphasis mb-1">Current average</div>
				<div class="text-body-1 font-weight-bold">
					{{ formatNumber(props.currentAverage, "money") }}
				</div>
			</div>
			<div style="width: 180px">
				<locale-number-input
					v-model="target"
					format="money"
					label="Average CPM"
					density="compact"
					hide-details="auto"
				/>
			</div>
			<v-btn
				variant="tonal"
				color="primary"
				:disabled="!target || target <= 0"
				:loading="calculating"
				@click="calculate"
			>
				Calculate
			</v-btn>
		</div>

		<template v-if="result">
			<v-alert v-if="result.reached" type="success" class="mb-3">
				Lands on {{ formatNumber(result.achieved, "money") }} with every month on
				budget.
			</v-alert>
			<v-alert v-else type="warning" class="mb-3">
				{{ formatNumber(target ?? 0, "money") }} can't be reached
				{{
					boundsText
						? `with every month on budget and every price ${boundsText}`
						: "with every month on budget — no month can sell more than all of its impressions"
				}}. The closest the year gets is
				{{ formatNumber(result.achieved, "money") }}.
			</v-alert>

			<v-table density="compact">
				<thead>
					<tr>
						<th>Month</th>
						<th class="text-right">Budget</th>
						<th class="text-right">Fill</th>
						<th class="text-right">CPM</th>
					</tr>
				</thead>
				<tbody>
					<tr v-for="(month, i) in months" :key="month">
						<td>{{ month }}</td>
						<td class="text-right">{{ formatNumberForScale("money", budget[i] ?? 0) }}</td>
						<td class="text-right text-no-wrap">
							<span class="text-medium-emphasis">{{ (fill[i] ?? 0).toFixed(1) }}%</span>
							<template v-if="result.months[i]">
								→ <strong>{{ result.months[i]!.fill.toFixed(1) }}%</strong>
							</template>
						</td>
						<td class="text-right text-no-wrap">
							<span class="text-medium-emphasis">{{ formatNumber(cpm[i] ?? 0, "money") }}</span>
							<template v-if="result.months[i]">
								→ <strong>{{ formatNumber(result.months[i]!.cpm, "money") }}</strong>
							</template>
						</td>
					</tr>
				</tbody>
			</v-table>
		</template>

		<template v-slot:actions>
			<v-spacer />
			<v-btn variant="text" color="error" @click="visible = false">Cancel</v-btn>
			<v-btn
				color="primary"
				variant="tonal"
				:disabled="!result || calculating"
				@click="apply"
			>
				Apply to months
			</v-btn>
		</template>
	</modal>
</template>
